import { ReactElement, ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { AppRoot } from '../components/AppRoot'
import { decideGate } from '../lib/gate'
import { useSession } from '../lib/session-context'
import { DisabledNotice } from './DisabledNotice'
import { NoProfileNotice } from './NoProfileNotice'
import { PendingNotice } from './PendingNotice'

/**
 * The five screens this app needs at its five URL paths, injected as props rather than imported
 * directly. `SignInScreen`, `JoinScreen`, `SetupScreen` and `AdminScreen` (Tasks 13-16) and
 * `AppShell` (rewired by Task 18) don't exist yet when this file is written — Task 18 (`App.tsx`)
 * is "the router host" and supplies the real ones. This is a deliberate dependency-injection
 * design recorded in this phase's task brief, not a placeholder to be removed later.
 */
export interface AppRoutesProps {
  /** Rendered at '/' (and the shell's own tab sub-paths — see below) when
   *  decideGate(session, profile) returns 'READY'. */
  shell: ReactNode
  /** Rendered at '/' when decideGate(session, profile) returns 'SETUP'. */
  setup: ReactNode
  /** Rendered at '/sign-in'. Reachable regardless of gate state. */
  signIn: ReactNode
  /** Rendered at '/join'. Reachable regardless of gate state. */
  join: ReactNode
  /** Rendered at '/admin', only when profile?.is_admin && profile.status === 'ACTIVE'. */
  admin: ReactNode
}

/**
 * The app's five routes and the auth/invite gate that decides what `/` shows.
 *
 * Exports `<Routes>…</Routes>` only — it assumes it is already rendered inside a router context
 * (`<BrowserRouter>`, supplied by `App.tsx`/Task 18) and does not provide one itself, so this
 * component cannot render meaningfully on its own outside a `MemoryRouter`/`BrowserRouter`.
 *
 * Reads `{ session, profile, loading }` from `useSession()` (Task 10) and calls
 * `decideGate(session, profile)` (Task 8) itself — this is the "later task" both of those
 * modules' own doc comments point to as the one responsible for that call.
 *
 * While `loading` is true, renders `null`. That window is at most one async round-trip and this
 * phase adds no dedicated loading UI for it — a later phase can add a spinner without touching
 * this file's contract.
 */
export function AppRoutes(props: AppRoutesProps): ReactElement | null {
  const { session, profile, loading } = useSession()

  if (loading) return null

  const gate = decideGate(session, profile)

  let rootContent: ReactNode
  switch (gate) {
    case 'SIGNED_OUT':
      rootContent = <Navigate to="/sign-in" replace />
      break
    case 'NO_PROFILE':
      rootContent = <NoProfileNotice />
      break
    case 'PENDING':
      rootContent = <PendingNotice />
      break
    case 'DISABLED':
      rootContent = <DisabledNotice />
      break
    case 'SETUP':
      rootContent = props.setup
      break
    case 'READY':
      rootContent = props.shell
      break
  }

  // Defense in depth beyond what's strictly reachable this phase: every admin is bootstrapped
  // ACTIVE by handle_new_user, so this can't currently produce a different answer than checking
  // is_admin alone — but it costs nothing and reads as intent, not an oversight.
  const isAdmin = profile?.is_admin === true && profile?.status === 'ACTIVE'

  return (
    <Routes>
      {/* A splat, not an exact "/": the shell owns the tab sub-paths too
          (/calendar, /swaps, /settings — mapped from the pathname by
          AppShell's tabFromPathname), and a URL nothing matches should show
          this route's gate verdict rather than a blank page. Route ranking
          keeps the four specific routes below winning regardless — a splat
          loses to any static path — so /sign-in, /join, /auth/callback and
          /admin are unaffected, and this only widens what "/" catches. */}
      <Route path="/*" element={rootContent} />
      {/* No redirect-away-if-already-signed-in: a signed-in user who navigates here manually
          just sees the sign-in screen render harmlessly. A deliberate simplification, not an
          oversight — see this task's brief. */}
      <Route path="/sign-in" element={props.signIn} />
      {/* Always reachable, signed out or signed in but PENDING — the common case is a cold
          invite-link click; the PENDING notice above also links here. */}
      <Route path="/join" element={props.join} />
      {/* No OAuth code-exchange logic here by design: supabase-js auto-detects a session from
          the URL (detectSessionInUrl) and fires it through the onAuthStateChange subscription
          SessionProvider already holds, so useSession() picks up the new session before this
          route's content would ever matter. Unreachable this phase regardless — nothing links
          to it — this route exists only so enabling a provider needs no code change. */}
      <Route
        path="/auth/callback"
        element={
          <AppRoot>
            <p className="sl-hint">Signing you in…</p>
          </AppRoot>
        }
      />
      <Route path="/admin" element={isAdmin ? props.admin : <Navigate to="/" replace />} />
    </Routes>
  )
}
